import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { FormControlLabel, MenuItem, Switch, TextField } from "@mui/material";
import Agenda from "./common/Agenda";
import Masthead from "./common/Masthead";
import Progress from "./common/Progress";
import TimeComponent from "./common/TimeComponent";
import { concatParams, CreateParamsJson } from "../helper/pathHelper";
import StartButton from "./common/StartButton";
import ResetButton from "./common/ResetButton";
import StarttimePicker from "./common/StarttimePicker";
import EditWithAi from "./common/EditWithAi";
import SharePlan from "./common/SharePlan";
import { calculateAddHHmmArrayToMoment } from "../helper/timeCalculations";
import { formatTotalDuration } from "../helper/useTimerData";
import useSoundSettings, {
  WARNING_LEAD_OPTIONS,
} from "../helper/useSoundSettings";
import { useLanguage } from "../i18n/LanguageContext";

const path = "/";

export default function Configuration() {
  const [config, setConfig] = useState(0);
  const [hidePast, setHidePast] = useState(false);
  const { language, t } = useLanguage();
  const { enabled, setEnabled, warningLeadSeconds, setWarningLeadSeconds } =
    useSoundSettings();

  const navigate = useNavigate();
  const json = CreateParamsJson();

  useEffect(() => {
    if (!json.starttime) {
      const starttime = moment().toISOString();
      json.starttime = starttime;
      navigate({
        pathname: path,
        search: concatParams({
          ...json,
        }),
      });
    }
  });

  const agenda = json.agenda ? JSON.parse(json.agenda) : [];
  const starttime = json.starttime ? moment(json.starttime) : null;

  // The start time itself isn't repeated here - it's right there in the
  // "Set timer" field below.
  let subtitle = t("board.noCards");
  if (starttime) {
    const endtime = calculateAddHHmmArrayToMoment(
      starttime.clone(),
      agenda.map((item) => item.time)
    );
    const totalSeconds = moment.duration(endtime.diff(starttime)).asSeconds();
    subtitle = t("board.cards", { count: agenda.length });
    if (totalSeconds > 0) {
      subtitle += ` · ${t("board.total", {
        duration: formatTotalDuration(totalSeconds, language),
      })}`;
    }
  }

  return (
    <div className="board">
      <div className="config-grid">
        <div className="config-timerblock">
          <div className="timer-dock">
            <Progress
              enabled={enabled}
              warningLeadSeconds={warningLeadSeconds}
              onToggleEnabled={setEnabled}
            />
          </div>

          <h1 className="config-addcard-heading">{t("board.addCard")}</h1>
          <section className="note config-addcard">
            <TimeComponent path={path} />
          </section>
        </div>

        <div className="config-title">
          <Masthead title="focussuite.de" subtitle={subtitle} />
        </div>

        <div className="config-agenda-head agenda-header">
          <h1>{t("board.onTheTable")}</h1>
          <FormControlLabel
            className="hide-past-toggle"
            control={
              <Switch
                size="small"
                checked={hidePast}
                onChange={(event) => setHidePast(event.target.checked)}
              />
            }
            label={t("board.hidePast")}
          />
          <div className="agenda-header-actions">
            <SharePlan />
            <EditWithAi />
          </div>
        </div>

        <section className="config-settimer note">
          <h2>{t("board.setTimer")}</h2>
          <div className="settimer-row">
            <StarttimePicker path={path} key={config} />
            <div className="btn-row">
              <ResetButton path={path} />
              <StartButton path={path} setConfig={setConfig} config={config} />
            </div>
          </div>
          <div className="settimer-sound-row">
            <FormControlLabel
              className="hide-past-toggle"
              control={
                <Switch
                  size="small"
                  checked={enabled}
                  onChange={(event) => setEnabled(event.target.checked)}
                />
              }
              label={t("board.soundAlerts")}
            />
            <TextField
              select
              variant="standard"
              size="small"
              className="sound-lead-select"
              label={t("board.warnBefore")}
              disabled={!enabled}
              value={warningLeadSeconds}
              onChange={(event) =>
                setWarningLeadSeconds(Number(event.target.value))
              }
            >
              {WARNING_LEAD_OPTIONS.map((option) => (
                <MenuItem key={option.seconds} value={option.seconds}>
                  {t(...option.label)}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </section>

        <div className="config-cards">
          <Agenda path={path} hidePast={hidePast} />
        </div>
      </div>
    </div>
  );
}
